import { useMemo, useState, useEffect, useRef } from 'react';
import { useButtonsStore } from '../../../store/models';
import { useErrorStore } from '../../../store/models/error';
import useErrors from '../../../store/models/error/useErrors';
import { serviceErrorSerializer } from '../../../store/models/buttons/errors/serviceErrors';
import useModuleErrorDialog from '../../../store/models/error/useModuleErrorDialog';

function useEcalWorldSwitch (props) {
    const { row } = props;
    const buttonsStore = useButtonsStore();
    const errorStore = useErrorStore();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [errorToShow, setErrorToShow] = useState({});

    // Only this row's switch interaction should own the error dialog.
    // getPublisherButton is called once by the list on mount (not per-row),
    // so we watch only setPublisherButton to avoid all rows showing a dialog simultaneously.
    const wasTriggered = useRef(false);

    const { errors, hasError } = useErrors({
        hook: 'useEcalWorldSwitch',
        shouldValidateInput: false,
        useTouched: false,
        input: {},
        inputValidator: () => ({}),
        serviceErrorSerializer,
        errorObservables: [
            errorStore.observed.errors.setPublisherButton,
        ],
    });

    useEffect(() => {
        if (wasTriggered.current && hasError && errors.message) {
            setErrorToShow(errors);
            wasTriggered.current = false;
        } else {
            setErrorToShow({});
        }
    }, [errors]);

    const { renderErrorDialog } = useModuleErrorDialog({ errors: errorToShow });

    const isPublic = useMemo(() => {
        const publisherButton = buttonsStore.publisherButton;
        return publisherButton?.id === row.id && publisherButton?.publicButton === true;
    }, [buttonsStore.observed.publisherButton, row.id]);

    const tooltipTitle = isPublic
        ? 'Discoverable in ECAL World — click to disable'
        : 'Make discoverable in ECAL World';

    const dialogTitle = isPublic ? 'Disable Primary Display' : 'Enable Primary Display';

    const dialogText = isPublic
        ? 'Are you sure you want to turn off this display for ECAL World?'
        : 'Are you sure you want to turn on this display for ECAL World?';

    function handleToggle (e) {
        e.stopPropagation();
        e.preventDefault();
        setConfirmOpen(true);
    }

    function handleConfirm () {
        wasTriggered.current = true;
        buttonsStore.setPublisherButton({
            buttonWidgetId: row.id,
            publicButton: !isPublic,
        });
        setConfirmOpen(false);
    }

    function handleCancel () {
        setConfirmOpen(false);
    }

    return {
        isPublic,
        confirmOpen,
        tooltipTitle,
        dialogTitle,
        dialogText,
        handleToggle,
        handleConfirm,
        handleCancel,
        renderErrorDialog,
    };
}

export default useEcalWorldSwitch;
